const { debounce } = window.BAO.Utils

class AccountForms extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setScrollMargin = this.setScrollMargin.bind(this)

    this.setScrollMargin()
    this.checkRecoverSuccess()
    this.checkRegisterSuccess()
    this.updateReturnTo()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      window,
      'resize',
      debounce(this.setScrollMargin.bind(this), 100),
    )

    this.handleSwitch = this.handleSwitch.bind(this)
    this.els?.switch?.elements?.forEach(switchEl => {
      this.listeners.add(switchEl, 'click', e => {
        if (e.target && e.target.hasAttribute('href')) {
          e.preventDefault()
        }

        this.handleSwitch(switchEl.dataset.type)
      })
    })
  }

  async successfulRegistration () {
    let attempts = 0
    const maxAttempts = 5
    let profile

    const email =
      this.els['register-email']?.element?.value ||
      sessionStorage.getItem('BAO-email') ||
      sessionStorage.getItem('BAO-customer[email]')

    if (!email) {
      // eslint-disable-next-line no-console
      console.log('No email found')
      return
    }

    // Sometimes Klaviyo takes a second to process the event, so we try up to 5 times with a 1 second delay between attempts.
    while (!profile && attempts < maxAttempts) {
      profile = await this.getKlaviyoProfile(email)

      if (!profile) {
        attempts++

        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts))
        }
      }
    }

    if (!profile) {
      // eslint-disable-next-line no-console
      console.log('No Klaviyo profile found')
      return
    }

    const id = profile[0].id
    const { properties } = profile[0].attributes
    const evergreenStep1Source = properties?.evergreen_step1_source
    if (!evergreenStep1Source) return

    const attributes = {
      email,
      properties: {
        evergreen_account_created: 'true',
        evergreen_account_creation_source: evergreenStep1Source,
      },
    }

    window.datalayer.push({
      event: 'evergreenAccountCreated',
      email,
    })

    await this.updateKlaviyoProfile(id, attributes)
  }

  async checkRegisterSuccess () {
    // form.posted_successfully not working for register form. Redirect to account page if
    // customer is logged in and querystring contains register=1
    if (!window.location.search.includes('register=1')) {
      return
    } else {
      await this.successfulRegistration()
    }

    if (this.dataset.customer === 'false') return

    window.location.href = this.dataset.accountUrl
  }

  checkRecoverSuccess () {
    // form.posted_successfully is not working for the recover form, so we have to do this.
    //
    // If the querystring contains sent=1 with the #recover hash, then show the success message.
    if (
      window.location.hash === '#recover' &&
      window.location.search.includes('sent=1')
    ) {
      this.externalEls.login.element.ariaHidden = true
      this.els.success.element.ariaHidden = false
      this.els['recover-instructions'].element.ariaHidden = true
      this.els['recover-form'].element.ariaHidden = true
    }
  }

  handleSwitch (type) {
    this.els.form.elements.forEach(formEl => {
      formEl.ariaHidden = type !== formEl.dataset.type
    })

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          private_sale_form_type: type,
        },
      }),
    })
  }

  setScrollMargin () {
    this.scrollMarginTop = Math.round(this.getBoundingClientRect().top)

    this.style.setProperty('--Scroll_MarginTop', `${this.scrollMarginTop}px`)
  }

  updateReturnTo () {
    const loginForm = this.els['login-form']?.element
    const overrideDestinationUrl =
      this.dataset.routesUrl && this.objectType && this.objectHandle
        ? `${this.dataset.routesUrl}${this.objectType}/${this.objectHandle}`
        : null

    if (!overrideDestinationUrl || !loginForm) return

    // Remove already-existing return_to input if it exists
    loginForm.querySelector('input[name="return_to"]')?.remove()
    loginForm.insertAdjacentHTML(
      'afterbegin',
      `<input type="hidden" name="return_to" value="${overrideDestinationUrl}">`,
    )

    // If the customer is logged in and a `overrideDestinationUrl` value is prsent, we
    // can assume the customer is trying to access a page that is beyond their tier. In
    // this case, we redirect them to the account page.
    if (this.dataset.customer === 'true') {
      window.location.href = this.dataset.accountUrl
    }
  }

  get urlParams () {
    return new URLSearchParams(window.location.search)
  }

  // Set this.collectionHandle based on the presence of the 'collection' query string
  get collectionHandle () {
    return this.urlParams.has('collection')
      ? this.urlParams.get('collection')
      : null
  }

  // Set this.productHandle based on the presence of the 'product' query string
  get productHandle () {
    return this.urlParams.has('product') ? this.urlParams.get('product') : null
  }

  get objectType () {
    if (this.collectionHandle) {
      return 'collections'
    } else if (this.productHandle) {
      return 'products'
    }
  }

  get objectHandle () {
    if (this.collectionHandle) {
      return this.collectionHandle
    } else if (this.productHandle) {
      return this.productHandle
    }
  }

  async getKlaviyoProfile (emailAddress) {
    const scriptUrl = 'https://self-portrait-klaviyo.vercel.app/api/get-profile'

    const data = {
      email: emailAddress,
      store: window.theme?.klaviyo?.store,
    }

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    const jsonResponse = await response.json()
    return jsonResponse
  }

  async updateKlaviyoProfile (id, attributes) {
    const body = {
      store: window.theme?.klaviyo?.store,
      id,
      attributes,
    }

    const scriptUrl =
      'https://self-portrait-klaviyo.vercel.app/api/update-profile'

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    const jsonResponse = await response.json()
    return jsonResponse
  }
}

if (!customElements.get('account-forms')) {
  customElements.define('account-forms', AccountForms)
}
